import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, Newspaper, Waves, Sun, TreePine, Cloud, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicArticles } from '../services/newsService';
import { fetchPublicSettings, pickCmsShort } from '../services/publicContentService';
import { absoluteMediaUrl } from '../admin/api';

const CATEGORIES = [
  { id: 'all',    ar: 'الكل',                en: 'All',                 icon: Sparkles, accent: 'slate'  },
  { id: 'marine', ar: 'بحرية',               en: 'Marine',              icon: Waves,    accent: 'blue'   },
  { id: 'desert', ar: 'الصحراء',             en: 'Desert',              icon: Sun,      accent: 'orange' },
  { id: 'forest', ar: 'الغابات والحياة البرية', en: 'Forests & Wildlife',icon: TreePine, accent: 'green'  },
  { id: 'air',    ar: 'الهواء والمناخ',       en: 'Air & Climate',       icon: Cloud,    accent: 'sky'    },
];

const ACCENT = {
  marine: { ring: 'hover:border-brand-blue/50',   chip: 'bg-brand-blue/10 text-brand-blue-800 border-brand-blue/25 dark:bg-brand-blue/20 dark:text-brand-blue-200 dark:border-brand-blue/40',     link: 'text-brand-blue-700 dark:text-brand-blue-300'   },
  desert: { ring: 'hover:border-brand-orange/50', chip: 'bg-brand-orange/10 text-brand-orange-800 border-brand-orange/25 dark:bg-brand-orange/20 dark:text-amber-200 dark:border-brand-orange/40', link: 'text-brand-orange-700 dark:text-amber-300'       },
  forest: { ring: 'hover:border-brand-green/50',  chip: 'bg-brand-green/10 text-brand-green-800 border-brand-green/25 dark:bg-brand-green/20 dark:text-emerald-200 dark:border-brand-green/40',   link: 'text-brand-green-700 dark:text-emerald-300'      },
  air:    { ring: 'hover:border-sky-400/60',      chip: 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-500/20 dark:text-sky-200 dark:border-sky-400/40',                                       link: 'text-sky-700 dark:text-sky-300'                  },
};

// Real photographic ecosystem panels (4-panel cinematic hero)
const ECOSYSTEMS = [
  {
    key: 'sky',
    icon: Cloud,
    label_ar: 'السماء', label_en: 'Sky',
    sub_ar: 'الهواء والمناخ', sub_en: 'Air & Climate',
    img_light: 'https://images.unsplash.com/photo-1601297183305-6df142704ea2?w=1600&q=80',
    img_dark:  'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1600&q=80',
    accent: 'sky',
  },
  {
    key: 'forest',
    icon: TreePine,
    label_ar: 'الغابات', label_en: 'Forest',
    sub_ar: 'الحياة الفطرية', sub_en: 'Wildlife',
    img_light: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600&q=80',
    img_dark:  'https://images.unsplash.com/photo-1511497584788-876760111969?w=1600&q=80',
    accent: 'green',
  },
  {
    key: 'desert',
    icon: Sun,
    label_ar: 'الصحراء', label_en: 'Desert',
    sub_ar: 'البيئة البرية', sub_en: 'Arid Lands',
    img_light: 'https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?w=1600&q=80',
    img_dark:  'https://images.unsplash.com/photo-1547234935-80c7145ec969?w=1600&q=80',
    accent: 'orange',
  },
  {
    key: 'sea',
    icon: Waves,
    label_ar: 'البحر', label_en: 'Sea',
    sub_ar: 'الحياة البحرية', sub_en: 'Marine',
    img_light: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=1600&q=80',
    img_dark:  'https://images.unsplash.com/photo-1439405326854-014607f694d7?w=1600&q=80',
    accent: 'blue',
  },
];

const NewsPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [activeCategory, setActiveCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // CMS-driven hero — eyebrow, title (prefix + highlight), subtitle, and up to
  // 4 ecosystem panel image overrides. Everything falls back to defaults.
  const [heroCms, setHeroCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('news_hero').then((v) => { if (alive) setHeroCms(v || {}); });
    return () => { alive = false; };
  }, []);

  const heroEyebrow    = pickCmsShort(heroCms.eyebrow, isAr)         || (isAr ? 'الأخبار والمستجدات' : 'News & Updates');
  const heroTitlePrefix = pickCmsShort(heroCms.title_prefix, isAr)   || (isAr ? 'رحلة في' : 'A journey across');
  const heroTitleHi    = pickCmsShort(heroCms.title_highlight, isAr) || (isAr ? 'أربع بيئات' : 'four ecosystems');
  const heroSubtitle   = pickCmsShort(heroCms.subtitle, isAr)        || (isAr
    ? 'من أعماق البحر، إلى رمال الصحراء، إلى ظلال الغابات، إلى أفق السماء.'
    : 'From the depths of the sea, through desert sands, beneath forest canopies, to the open sky.');

  // Panel image overrides (indexed by ECOSYSTEMS array order)
  const heroPanels = Array.isArray(heroCms.panels) ? heroCms.panels : [];

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPublicArticles(language)
      .then((list) => { if (active) setArticles(list); })
      .catch(() => { if (active) setArticles([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [language]);

  const FEATURED = articles[0] || null;
  const REST = useMemo(() => articles.slice(1), [articles]);
  const filtered = useMemo(() => (
    activeCategory === 'all'
      ? REST
      : REST.filter((a) => a.category === activeCategory)
  ), [REST, activeCategory]);

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="relative overflow-hidden bg-white dark:bg-[#070d18]">
      {/* ============================================================
          CINEMATIC HERO — 4 real photographic panels (Sky · Forest · Desert · Sea)
          Editorial split-screen style. Premium, realistic, not illustrated.
         ============================================================ */}
      <section className="relative h-[100vh] min-h-[700px] w-full overflow-hidden">
        {/* The 4 vertical photo panels */}
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-4">
          {ECOSYSTEMS.map((eco, ecoIdx) => {
            const override = heroPanels[ecoIdx] || {};
            const imgLight = (override.img_light && String(override.img_light).trim()) || eco.img_light;
            const imgDark  = (override.img_dark  && String(override.img_dark).trim())  || eco.img_dark;
            const labelAr  = (override.label_ar  || eco.label_ar);
            const labelEn  = (override.label_en  || eco.label_en);
            const subAr    = (override.sub_ar    || eco.sub_ar);
            const subEn    = (override.sub_en    || eco.sub_en);
            return (
            <div key={eco.key} className="relative group overflow-hidden">
              {/* Light photo */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-all duration-[1200ms] ease-out group-hover:scale-[1.04] dark:opacity-0"
                style={{ backgroundImage: `url('${imgLight}')` }}
              />
              {/* Dark photo */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-0 dark:opacity-100 transition-all duration-[1200ms] ease-out group-hover:scale-[1.04]"
                style={{ backgroundImage: `url('${imgDark}')` }}
              />
              {/* Editorial darkening from bottom for legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/35 dark:from-black/85 dark:via-black/35 dark:to-black/55" />
              {/* Subtle desaturation for cohesion */}
              <div className="absolute inset-0 bg-slate-900/10 dark:bg-slate-950/30 mix-blend-multiply pointer-events-none" />

              {/* Panel caption (bottom) */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-2 text-white/80">
                  <eco.icon className="w-4 h-4" strokeWidth={1.8} />
                  <span className="text-[10px] tracking-[0.35em] uppercase font-semibold">
                    {isAr ? subAr : subEn}
                  </span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white tracking-tight"
                     style={{ textShadow: '0 2px 14px rgba(0,0,0,0.6)' }}>
                  {isAr ? labelAr : labelEn}
                </div>
                {/* Brand-tinted underline */}
                <div className={`mt-3 h-px w-12 ${
                  eco.accent === 'sky'    ? 'bg-sky-300'           :
                  eco.accent === 'green'  ? 'bg-brand-green-300'   :
                  eco.accent === 'orange' ? 'bg-brand-orange-300'  :
                                            'bg-brand-blue-300'
                }`} />
              </div>

              {/* Vertical divider line between panels (desktop) */}
              <div className="hidden md:block absolute top-0 bottom-0 right-0 w-px bg-white/20 dark:bg-white/10 last:hidden" />
            </div>
            );
          })}
        </div>

        {/* Cinematic center vignette + soft top fade */}
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(0,0,0,0.45)_100%)]" />
        <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none bg-gradient-to-b from-black/40 to-transparent" />

        {/* HERO COPY OVERLAY */}
        <div className="relative z-10 h-full flex items-center justify-center container mx-auto px-4">
          <div className="text-center max-w-4xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8
              bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
              <Newspaper className="w-4 h-4 text-white" />
              <span data-testid="news-hero-eyebrow" className="text-xs tracking-[0.45em] uppercase text-white font-semibold">
                {heroEyebrow}
              </span>
            </div>

            <h1 data-testid="news-hero-title" className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 leading-[1.05] text-white"
                style={{ textShadow: '0 4px 30px rgba(0,0,0,0.7), 0 0 60px rgba(0,0,0,0.3)' }}>
              {heroTitlePrefix}{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-300 via-emerald-300 to-amber-300">
                {heroTitleHi}
              </span>
            </h1>

            <p data-testid="news-hero-subtitle" className="text-base md:text-lg max-w-2xl mx-auto leading-loose text-white/90"
               style={{ textShadow: '0 2px 14px rgba(0,0,0,0.7)' }}>
              {heroSubtitle}
            </p>

            <div className="mt-12 flex justify-center">
              <a href="#news-content"
                 className="inline-flex flex-col items-center gap-2 text-white/85 hover:text-white transition-colors">
                <span className="text-[11px] tracking-[0.4em] uppercase font-semibold">
                  {isAr ? 'استكشف' : 'Explore'}
                </span>
                <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          CONTENT — soft neutral with subtle 4-ecosystem corner halos
         ============================================================ */}
      <div id="news-content" className="relative bg-gradient-to-b from-white via-slate-50 to-white dark:from-[#070d18] dark:via-[#0a1424] dark:to-[#070d18]">
        <div className="pointer-events-none absolute top-20 left-0 w-[400px] h-[400px] rounded-full blur-[120px] bg-brand-blue/12 dark:bg-brand-blue/8" />
        <div className="pointer-events-none absolute top-[40%] right-0 w-[420px] h-[420px] rounded-full blur-[140px] bg-brand-green/12 dark:bg-brand-green/8" />
        <div className="pointer-events-none absolute bottom-[20%] left-[10%] w-[380px] h-[380px] rounded-full blur-[120px] bg-brand-orange/12 dark:bg-brand-orange/8" />
        <div className="pointer-events-none absolute bottom-0 right-[10%] w-[360px] h-[360px] rounded-full blur-[120px] bg-sky-400/12 dark:bg-sky-400/8" />

        {/* FEATURED — only when there is at least one published article */}
        {FEATURED && (
        <section className="relative py-24">
          <div className="container mx-auto px-4">
            <div className={`flex items-center gap-3 mb-10 ${isAr ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                {isAr ? 'الخبر المميز' : 'Featured Story'}
              </h2>
              <div className={`h-px flex-1 bg-gradient-to-${isAr ? 'l' : 'r'} from-brand-orange/40 to-transparent`} />
            </div>

            <article
              data-testid="featured-article"
              className="group relative grid md:grid-cols-5 gap-0 rounded-3xl overflow-hidden
                bg-white/90 dark:bg-white/[0.04]
                border border-slate-200/80 dark:border-white/10
                shadow-[0_30px_60px_-20px_rgba(58,122,186,0.18)]
                hover:shadow-[0_30px_60px_-20px_rgba(58,122,186,0.3)]
                backdrop-blur-xl transition-shadow duration-500"
            >
              <div className={`md:col-span-3 relative h-72 md:h-auto min-h-[340px] ${isAr ? 'md:order-2' : ''}`}>
                <img src={FEATURED.image} alt={FEATURED.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
                <div className={`absolute top-5 ${isAr ? 'left-5' : 'right-5'}`}>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-orange text-white text-xs font-bold shadow-lg">
                    <Sparkles className="w-3 h-3" />
                    {isAr ? 'مميّز' : 'Featured'}
                  </span>
                </div>
              </div>

              <div className={`md:col-span-2 p-8 lg:p-10 flex flex-col justify-center ${isAr ? 'text-right md:order-1' : 'text-left'}`}>
                <div className={`flex items-center gap-3 mb-5 ${isAr ? 'flex-row-reverse' : ''}`}>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${(ACCENT[FEATURED.category] || ACCENT.marine).chip}`}>
                    <Tag className="w-3 h-3" />
                    {FEATURED.tag}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 ${isAr ? 'flex-row-reverse' : ''}`}>
                    <Calendar className="w-3 h-3" />
                    {FEATURED.date}
                  </span>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-snug text-slate-900 dark:text-white">
                  {FEATURED.title}
                </h3>
                <p className="text-base text-slate-600 dark:text-slate-300 leading-loose mb-6">
                  {FEATURED.excerpt}
                </p>

                <Link to={`/news/${FEATURED.id}`} data-testid={`read-more-featured-${FEATURED.id}`} className={`${isAr ? 'self-end' : 'self-start'} inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all ${isAr ? 'flex-row-reverse' : ''}`}>
                  {isAr ? 'اقرأ المزيد' : 'Read More'}
                  <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                </Link>
              </div>
            </article>
          </div>
        </section>
        )}

        {/* CATEGORY FILTER */}
        <section className="relative pb-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const isActive = activeCategory === c.id;
                const activeGrad = {
                  slate:  'from-brand-blue to-brand-green',
                  blue:   'from-cyan-400 to-brand-blue',
                  orange: 'from-amber-400 to-brand-orange',
                  green:  'from-emerald-400 to-brand-green',
                  sky:    'from-sky-300 to-brand-blue-400',
                }[c.accent];
                return (
                  <button
                    key={c.id}
                    data-testid={`news-cat-${c.id}`}
                    onClick={() => setActiveCategory(c.id)}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                      isActive
                        ? `bg-gradient-to-r ${activeGrad} text-white shadow-lg shadow-black/10 dark:shadow-black/40`
                        : 'bg-white/80 dark:bg-white/[0.05] text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-white/[0.1] border border-slate-200 dark:border-white/10 backdrop-blur-md'
                    } ${isAr ? 'flex-row-reverse' : ''}`}
                  >
                    <Icon className="w-4 h-4" />
                    {isAr ? c.ar : c.en}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* NEWS GRID */}
        <section className="relative pt-10 pb-28">
          <div className="container mx-auto px-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 max-w-7xl mx-auto">
              {loading ? (
                <div className="col-span-full text-center py-20 text-slate-500 dark:text-slate-400">
                  {isAr ? 'جارٍ تحميل الأخبار…' : 'Loading news…'}
                </div>
              ) : filtered.length === 0 ? (
                <div className="col-span-full text-center py-20 text-slate-500 dark:text-slate-400">
                  {isAr ? 'لا توجد أخبار في هذه الفئة حالياً.' : 'No news in this category yet.'}
                </div>
              ) : (
                filtered.map((article, i) => {
                  const acc = ACCENT[article.category] || ACCENT.marine;
                  return (
                    <article
                      key={article.id}
                      data-testid={`news-card-${article.id}`}
                      className={`group relative rounded-2xl overflow-hidden
                        bg-white/90 dark:bg-white/[0.04]
                        border border-slate-200 dark:border-white/10
                        ${acc.ring}
                        hover:shadow-2xl hover:shadow-black/5 dark:hover:shadow-black/40
                        backdrop-blur-xl
                        transition-all duration-500 hover:-translate-y-1.5 animate-fade-in-up`}
                      style={{ animationDelay: `${i * 0.06}s` }}
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img src={absoluteMediaUrl(article.image)} alt={article.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                        <div className={`absolute top-3 ${isAr ? 'left-3' : 'right-3'}`}>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur-md ${acc.chip}`}>
                            <Tag className="w-3 h-3" />
                            {article.tag}
                          </span>
                        </div>
                      </div>

                      <div className={`p-6 ${isAr ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-3 ${isAr ? 'flex-row-reverse' : ''}`}>
                          <Calendar className="w-3 h-3" />
                          <span>{article.date}</span>
                        </div>

                        <h3 className="text-lg font-bold mb-3 leading-snug text-slate-900 dark:text-white transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-4 line-clamp-3">
                          {article.excerpt}
                        </p>

                        <Link to={`/news/${article.id}`} data-testid={`read-more-${article.id}`} className={`inline-flex items-center gap-1.5 text-sm font-semibold ${acc.link} hover:gap-2.5 transition-all ${isAr ? 'flex-row-reverse' : ''}`}>
                          {isAr ? 'اقرأ المزيد' : 'Read More'}
                          <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                        </Link>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default NewsPage;
